import type { ProducerNumber, V4FuneralHome } from '@/apiTypes'
import type Plan from '@/classes/Plan'
import { OapProvider } from '@/data/oapProviders'
import { PlanOapPaymentType } from '@/enums/PlanOapPaymentType'
import { SepioPaymentPlanInterval } from '@/enums/SepioPaymentPlanInterval'
import { useOapPlanProvider } from '@/mixins/oapPlanProvider'
import { useRequestQueue } from '@/mixins/requestQueue'
import { windowRequestQueueConfig } from '@/mixins/requestQueueWindowConfig'
import { purchaseApiAxiosInstance } from '@/services/purchaseApiAxiosInstance'
import { useRetryMethods } from '@/services/retryUtils'
import type { SubmissionService } from '@/services/types'
import { useAuthStore } from '@/stores/auth'
import { useCrmStore } from '@/stores/crm'
import type {
	SalesContact,
	SepioCreditCard,
	SepioEftPaymentDetails,
	SepioEnrollRequest,
	SepioPaymentDetails,
} from '@/types'
import { captureException } from '@sentry/vue'
import type { AxiosRequestConfig } from 'axios'
import localforageLib from 'localforage'
import { DateTime } from 'luxon'

const localSepioSubmissions = localforageLib.createInstance({
	name: 'request-queue',
	storeName: 'sepioSubmissions',
})

const sepioSubmissionsQueueConfig = {
	axiosInstance: purchaseApiAxiosInstance,
	requestBackgroundSync: async (tag: string) => {
		await windowRequestQueueConfig.requestBackgroundSync('submission-queue')
	},
}

const sepioSubmissionRequestQueue = useRequestQueue(
	'sepioSubmissions',
	sepioSubmissionsQueueConfig
)

// This getter exposes the sepio request queue to submission store
export const getQueue = () => {
	return sepioSubmissionRequestQueue
}

// This method checks whether OAP should submit (has token)
// if so, it creates the sepio enrollment request and adds it to the queue
export const submitRequest = async (plan: Plan) => {
	// skip sending OAP if they used the offline form
	if (!shouldSubmit(plan)) {
		return
	}

	const crmStore = useCrmStore()
	const funeralHome = crmStore.findFuneralHome(plan.funeralHomeId)

	const data = mapOapSubmission(plan, funeralHome)

	const sepioRequestConfig: AxiosRequestConfig = {
		url: 'v2/oap/sepio/enroll',
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		data,
	}

	try {
		await sepioSubmissionRequestQueue.addRequest(plan.id, sepioRequestConfig)
	} catch (error: any) {
		// only log to sentry, executeRequest takes care of updating appropriate status in the localForage
		captureException(error, {
			extra: {
				message: 'Error submitting enrollment for Sepio.',
				response: error.response?.data,
			},
		})
	}
}

const mapOapSubmission = (
	plan: Plan,
	funeralHome: V4FuneralHome | undefined
): SepioEnrollRequest => {
	const birthdayDate = DateTime.fromISO(plan.birthDate ?? '')
	const birthDay = birthdayDate.day.toString()
	const birthMonth = birthdayDate.month.toString()
	const birthYear = birthdayDate.year.toString()
	const oapProvider = useOapPlanProvider(plan.oapPayment.provider)
	const selectedPlan = oapProvider.getPlanById(plan.oapPayment.paymentPlanId)

	const intervals =
		selectedPlan?.paymentCount === 1
			? SepioPaymentPlanInterval.Year
			: SepioPaymentPlanInterval.Month

	let paymentType: 'ach' | 'stripe' = 'ach'
	let paymentDetails: Record<string, unknown> = {}

	const paymentPlan = plan.oapPayment.paymentPlan
	if (paymentPlan && 'paymentType' in paymentPlan) {
		paymentType =
			paymentPlan.paymentType === PlanOapPaymentType.CreditCard
				? 'stripe'
				: 'ach'

		if (isCardPayment(paymentPlan.paymentDetails)) {
			paymentDetails = {
				stripeCustomerSource: paymentPlan.paymentDetails?.token,
			}
		} else {
			paymentDetails = {
				bankAccountHolder: paymentPlan.paymentDetails?.accountHolder,
				bankAccountType: paymentPlan.paymentDetails?.accountType,
				bankRoutingNumber: paymentPlan.paymentDetails?.routingNumber,
				bankAccountNumber: paymentPlan.paymentDetails?.accountNumber,
			}
		}
	}
	const authStore = useAuthStore()
	const salesforceContactAfpId = authStore.getActiveSalesContactId()!
	const salespersonSepioId =
		authStore?.user?.salesContact?.producerNumbers?.data.find(
			(item: ProducerNumber) => item.vendor === 'Sepio Guard API'
		)?.producerNumber || ''

	const funeralHomeName = funeralHome?.name
	const funeralHomeId = funeralHome?.id

	return {
		planId: plan.id,
		salesforceContactAfpId,
		firstName: plan.firstName,
		lastName: plan.lastName,
		emailAddress: plan.email,
		phone: plan.phone,
		birthDay,
		birthMonth,
		birthYear,
		street: plan.address,
		city: plan.city,
		state: plan.state,
		country: 'USA',
		zip: plan.zip,
		intervalCount: selectedPlan?.paymentCount.toString() ?? '',
		intervals,
		paymentPlan: selectedPlan?.id ?? '',
		paymentPlanTitle: selectedPlan?.title ?? '',
		paymentType,
		...paymentDetails,
		salesperson: salespersonSepioId,
		price: `${selectedPlan?.price}`,
		isPartner: 0,
		otherFuneralHome: funeralHomeName,
		funeralHomeId,
		vendor: selectedPlan?.pricePointId ?? '',
	}
}

const shouldSubmit = (plan: Plan): boolean => {
	return (
		plan.completedSteps.includes('OAP Enrollment') &&
		!sepioDoneOffline(plan) &&
		plan.oapPayment.provider === OapProvider.SEPIO_GUARD
	)
}

const isCardPayment = (obj: any): obj is SepioCreditCard => {
	return (
		typeof obj === 'object' &&
		(typeof obj.token === 'string' ||
			typeof obj.sepioMembershipNumber === 'string')
	)
}

const sepioDoneOffline = (data: Plan): boolean => {
	const paymentPlan = data.oapPayment.paymentPlan
	if (!paymentPlan || !('paymentDetails' in paymentPlan)) return false

	return (
		isCardPayment(paymentPlan.paymentDetails) &&
		!!paymentPlan.paymentDetails?.sepioMembershipNumber
	)
}

export const validateRequest = (planHolder: Plan): boolean => {
	// We don't need payment warning if sepio registration is done through the app
	// it has already been paid
	if (sepioDoneOffline(planHolder)) {
		return true
	}

	const paymentPlan = planHolder.oapPayment.paymentPlan as SepioPaymentDetails

	switch (paymentPlan.paymentType) {
		case PlanOapPaymentType.EFT:
			return validateEftPaymentDetails(
				paymentPlan.paymentDetails as SepioEftPaymentDetails['paymentDetails']
			)
		case PlanOapPaymentType.CreditCard:
			return !!paymentPlan.paymentDetails?.token
		default:
			return false
	}
}

const validateEftPaymentDetails = (
	paymentDetails: SepioEftPaymentDetails['paymentDetails']
): boolean => {
	const {
		bankName,
		routingNumber_confirm,
		accountNumber_confirm,
		...propsThatMustExist
	} = paymentDetails

	const validPaymentDetails = Object.values(propsThatMustExist).every(
		(value) => value
	)

	return validPaymentDetails
}

export const sepioService: SubmissionService = {
	getQueue,
	submitRequest,
	validateRequest,
	serviceName: 'Sepio',
	storeName: 'sepioSubmissions',
	shouldSubmit,
	...useRetryMethods(
		sepioSubmissionRequestQueue,
		localSepioSubmissions,
		'Sepio'
	),
}
